import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  UploadCloud,
  Sparkles,
  Plus,
  Trash2,
  ChevronLeft,
  Save,
  Receipt,
  Eye,
  EyeOff,
  ArrowLeftRight,
} from "lucide-react";
import { api, API } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

const emptyParty = () => ({ name: "", address: "", city: "", state: "", state_code: "", gstin: "", pan: "" });
const emptyItem = () => ({ description: "", hsn: "", quantity: 1, unit: "Pcs", unit_price: 0, gst_rate: 18 });

export default function ReverseInvoice() {
  const navigate = useNavigate();
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [bill, setBill] = useState(null); // extracted + editable data
  const [showAllFields, setShowAllFields] = useState(false);

  const [confirming, setConfirming] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    const ok = /\.(pdf|png|jpg|jpeg|webp)$/i.test(file.name);
    if (!ok) {
      toast.error("Upload a PDF or image bill");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/bill/extract", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      // Auto-swap: original seller -> new buyer, original buyer -> new seller
      const originalSeller = { ...emptyParty(), ...(data.seller || {}) };
      const originalBuyer = { ...emptyParty(), ...(data.buyer || {}) };
      setBill({
        invoice_number: data.invoice_number || "",
        invoice_date: data.invoice_date || "",
        seller: originalBuyer,
        buyer: originalSeller,
        line_items: (data.line_items || []).map((i) => ({ ...emptyItem(), ...i })),
        tax_type: data.tax_type && data.tax_type !== "auto" ? data.tax_type : "auto",
        freight_charges: Number(data.freight_charges) || 0,
        packing_charges: Number(data.packing_charges) || 0,
        notes: data.notes || "",
        bill_file_id: data.bill_file_id || null,
      });
      setShowAllFields(false);
      toast.success("Bill extracted — seller & buyer swapped. Review and confirm.");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Failed to extract bill");
    } finally {
      setUploading(false);
    }
  };

  const upd = (path, value) => {
    setBill((prev) => {
      const next = { ...prev };
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = { ...cur[parts[i]] };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const updItem = (idx, key, value) =>
    setBill((p) => ({
      ...p,
      line_items: p.line_items.map((x, i) => (i === idx ? { ...x, [key]: value } : x)),
    }));

  const addItem = () => setBill((p) => ({ ...p, line_items: [...p.line_items, emptyItem()] }));
  const removeItem = (idx) =>
    setBill((p) => ({ ...p, line_items: p.line_items.filter((_, i) => i !== idx) }));

  const swapSellerBuyer = () =>
    setBill((p) => ({ ...p, seller: p.buyer, buyer: p.seller }));

  // Field is "filled" if it has a meaningful value
  const hasVal = (v) => {
    if (v === null || v === undefined) return false;
    if (typeof v === "number") return v !== 0;
    const s = String(v).trim();
    if (s === "" || s === "0") return false;
    return true;
  };
  const show = (v) => showAllFields || hasVal(v);

  const subtotal = (bill?.line_items || []).reduce(
    (s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0),
    0,
  );
  const totalTax = (bill?.line_items || []).reduce(
    (s, i) =>
      s + ((Number(i.quantity) || 0) * (Number(i.unit_price) || 0) * (Number(i.gst_rate) || 0)) / 100,
    0,
  );
  const freight = Number(bill?.freight_charges) || 0;
  const packing = Number(bill?.packing_charges) || 0;
  const grandTotal = subtotal + totalTax + freight + packing;

  const confirmGenerate = async () => {
    if (!bill) return;
    if (!bill.buyer.name) {
      toast.error("Buyer name is required");
      return;
    }
    if (!bill.line_items.length) {
      toast.error("Add at least one line item");
      return;
    }
    setConfirming(true);
    try {
      const payload = {
        po_number: "",
        po_date: "",
        invoice_number: bill.invoice_number || null,
        invoice_date: bill.invoice_date || null,
        buyer: bill.buyer,
        ship_to: bill.buyer,
        seller: bill.seller,
        line_items: bill.line_items.map((i) => ({
          ...i,
          quantity: Number(i.quantity) || 0,
          unit_price: Number(i.unit_price) || 0,
          gst_rate: Number(i.gst_rate) || 0,
        })),
        place_of_supply: bill.buyer.state || "",
        notes: bill.notes || "",
        bill_file_id: bill.bill_file_id,
        freight_charges: freight,
        packing_charges: packing,
        tax_type: bill.tax_type,
      };
      const { data: inv } = await api.post("/invoice/generate", payload);
      toast.success(`Invoice ${inv.invoice_number} generated`);
      navigate(`/invoice/${inv.id}`);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Failed to generate invoice");
    } finally {
      setConfirming(false);
    }
  };

  // ---------- UI ----------
  if (!bill) {
    return (
      <div className="p-8 max-w-5xl">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">
            Reverse Invoice
          </div>
          <h1 className="text-4xl font-bold tracking-tight" data-testid="reverse-heading">
            Convert any bill into your invoice
          </h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-xl">
            Upload a scanned bill or someone else's invoice. Our AI reads every
            field — you review and edit freely, then click Confirm to issue it in
            your own GST format.
          </p>
        </div>

        <Card className="p-2">
          <label
            className={`dropzone ${drag ? "active" : ""} rounded-md flex flex-col items-center justify-center py-16 cursor-pointer`}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDrag(false);
              handleFile(e.dataTransfer.files?.[0]);
            }}
            data-testid="bill-dropzone"
          >
            <input
              type="file"
              accept="application/pdf,image/png,image/jpeg,image/webp,.pdf,.png,.jpg,.jpeg,.webp"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="bill-file-input"
            />
            <div className="w-14 h-14 rounded-full bg-violet-50 flex items-center justify-center mb-4">
              <UploadCloud size={28} strokeWidth={1.5} className="text-violet-600" />
            </div>
            <div className="text-base font-medium">
              {uploading ? "Reading bill with AI…" : "Drop a Bill (PDF or Image)"}
            </div>
            <div className="text-sm text-zinc-500 mt-1">
              PDF, PNG, JPG, WEBP supported
            </div>
            {uploading && (
              <Badge variant="outline" className="mt-4 mono text-[10px]">
                <Sparkles size={10} className="mr-1" /> Gemini 3 Flash
              </Badge>
            )}
          </label>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-5 h-screen">
      {/* Left: source bill */}
      <div className="col-span-2 border-r border-zinc-200 bg-zinc-100 flex flex-col">
        <div className="px-5 py-3 border-b border-zinc-200 bg-white flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-zinc-500">Source Bill</div>
            <div className="text-sm font-medium">AI-extracted</div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setBill(null)} data-testid="back-upload">
            <ChevronLeft size={14} className="mr-1" /> New upload
          </Button>
        </div>
        <div className="flex-1 overflow-hidden">
          {bill.bill_file_id && (
            <iframe
              src={`${API}/bill/${bill.bill_file_id}/file`}
              className="w-full h-full"
              title="Bill preview"
              data-testid="bill-preview-iframe"
            />
          )}
        </div>
      </div>

      {/* Right: edit form */}
      <div className="col-span-3 overflow-y-auto flex flex-col">
        <div className="px-6 py-3 border-b border-zinc-200 bg-white sticky top-0 z-10 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-violet-600 flex items-center gap-1">
              <Sparkles size={12} /> Reverse Invoice — Seller & Buyer swapped
            </div>
            <div className="text-base font-semibold">Edit then Confirm</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAllFields((s) => !s)}
              data-testid="toggle-show-all-fields"
              title={showAllFields ? "Hide empty fields" : "Show all fields (even empty)"}
            >
              {showAllFields ? <EyeOff size={14} className="mr-1" /> : <Eye size={14} className="mr-1" />}
              {showAllFields ? "Hide Empty" : "Show All Fields"}
            </Button>
            <Button
              className="bg-violet-600 hover:bg-violet-700"
              onClick={confirmGenerate}
              disabled={confirming}
              data-testid="confirm-generate-btn"
            >
              <Save size={14} className="mr-1" />
              {confirming ? "Generating…" : "Confirm & Generate Invoice"}
            </Button>
          </div>
        </div>

        <div className="p-6 space-y-5 pb-28">
          {/* Invoice meta */}
          {(show(bill.invoice_number) || show(bill.invoice_date)) && (
            <Section title="Invoice Details">
              <div className="grid grid-cols-2 gap-3">
                {show(bill.invoice_number) && (
                  <F label="Invoice Number" value={bill.invoice_number} onChange={(v) => upd("invoice_number", v)} testid="rev-invoice-number" />
                )}
                {show(bill.invoice_date) && (
                  <F label="Invoice Date" value={bill.invoice_date} onChange={(v) => upd("invoice_date", v)} testid="rev-invoice-date" />
                )}
              </div>
            </Section>
          )}

          {/* Seller */}
          <Section
            title="Seller / Supplier (You)"
            right={
              <Button variant="ghost" size="sm" onClick={swapSellerBuyer} data-testid="rev-swap-parties" title="Swap seller and buyer">
                <ArrowLeftRight size={14} className="mr-1" /> Swap
              </Button>
            }
          >
            {(() => {
              const s = bill.seller;
              const anyVisible =
                show(s.name) || show(s.gstin) || show(s.address) || show(s.city) ||
                show(s.state) || show(s.state_code) || show(s.pan);
              if (!anyVisible) {
                return (
                  <div className="text-xs text-zinc-400 italic">
                    No seller fields extracted. Click <span className="font-medium">Show All Fields</span> to add them manually.
                  </div>
                );
              }
              return (
                <Grid>
                  {show(s.name) && <F label="Name" value={s.name} onChange={(v) => upd("seller.name", v)} testid="rev-seller-name" />}
                  {show(s.gstin) && <F label="GSTIN" value={s.gstin} onChange={(v) => upd("seller.gstin", v)} testid="rev-seller-gstin" />}
                  {show(s.address) && <F label="Address" wide value={s.address} onChange={(v) => upd("seller.address", v)} testid="rev-seller-address" />}
                  {show(s.city) && <F label="City" value={s.city} onChange={(v) => upd("seller.city", v)} testid="rev-seller-city" />}
                  {show(s.state) && <F label="State" value={s.state} onChange={(v) => upd("seller.state", v)} testid="rev-seller-state" />}
                  {show(s.state_code) && <F label="State Code" value={s.state_code} onChange={(v) => upd("seller.state_code", v)} testid="rev-seller-state-code" />}
                  {show(s.pan) && <F label="PAN" value={s.pan} onChange={(v) => upd("seller.pan", v)} testid="rev-seller-pan" />}
                </Grid>
              );
            })()}
          </Section>

          {/* Buyer */}
          <Section title="Buyer / Customer">
            {(() => {
              const b = bill.buyer;
              const anyVisible =
                show(b.name) || show(b.gstin) || show(b.address) || show(b.city) ||
                show(b.state) || show(b.state_code) || show(b.pan);
              if (!anyVisible) {
                return (
                  <div className="text-xs text-zinc-400 italic">
                    No buyer fields extracted. Click <span className="font-medium">Show All Fields</span> to add them manually.
                  </div>
                );
              }
              return (
                <Grid>
                  {show(b.name) && <F label="Name" value={b.name} onChange={(v) => upd("buyer.name", v)} testid="rev-buyer-name" />}
                  {show(b.gstin) && <F label="GSTIN" value={b.gstin} onChange={(v) => upd("buyer.gstin", v)} testid="rev-buyer-gstin" />}
                  {show(b.address) && <F label="Address" wide value={b.address} onChange={(v) => upd("buyer.address", v)} testid="rev-buyer-address" />}
                  {show(b.city) && <F label="City" value={b.city} onChange={(v) => upd("buyer.city", v)} testid="rev-buyer-city" />}
                  {show(b.state) && <F label="State" value={b.state} onChange={(v) => upd("buyer.state", v)} testid="rev-buyer-state" />}
                  {show(b.state_code) && <F label="State Code" value={b.state_code} onChange={(v) => upd("buyer.state_code", v)} testid="rev-buyer-state-code" />}
                  {show(b.pan) && <F label="PAN" value={b.pan} onChange={(v) => upd("buyer.pan", v)} testid="rev-buyer-pan" />}
                </Grid>
              );
            })()}
          </Section>

          {/* Line items */}
          <Section title="Line Items" right={
            <Button variant="outline" size="sm" onClick={addItem} data-testid="rev-add-item">
              <Plus size={14} className="mr-1" /> Add Row
            </Button>
          }>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border border-zinc-200">
                <thead className="bg-zinc-50 text-zinc-500">
                  <tr>
                    <th className="p-2 text-left w-8">#</th>
                    <th className="p-2 text-left">Description</th>
                    <th className="p-2 text-left w-24">HSN</th>
                    <th className="p-2 w-20 text-right">Qty</th>
                    <th className="p-2 w-24 text-right">Rate</th>
                    <th className="p-2 w-20 text-right">GST %</th>
                    <th className="p-2 w-24 text-right">Amount</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {bill.line_items.map((it, i) => {
                    const q = Number(it.quantity) || 0;
                    const r = Number(it.unit_price) || 0;
                    const g = Number(it.gst_rate) || 0;
                    const amt = q * r * (1 + g / 100);
                    return (
                      <tr key={i} className="border-t border-zinc-100" data-testid={`rev-item-row-${i}`}>
                        <td className="p-1.5 text-center mono text-zinc-500">{i + 1}</td>
                        <td className="p-1.5">
                          <Textarea
                            value={it.description}
                            onChange={(e) => updItem(i, "description", e.target.value)}
                            className="text-xs px-2 py-1 min-h-[36px] leading-snug"
                            data-testid={`rev-item-desc-${i}`}
                          />
                        </td>
                        <td className="p-1.5">
                          <Input value={it.hsn} onChange={(e) => updItem(i, "hsn", e.target.value)} className="h-8 text-xs mono px-2" data-testid={`rev-item-hsn-${i}`} />
                        </td>
                        <td className="p-1.5">
                          <Input type="number" value={it.quantity} onChange={(e) => updItem(i, "quantity", e.target.value)} className="h-8 text-xs mono text-right px-2" data-testid={`rev-item-qty-${i}`} />
                        </td>
                        <td className="p-1.5">
                          <Input type="number" step="0.01" value={it.unit_price} onChange={(e) => updItem(i, "unit_price", e.target.value)} className="h-8 text-xs mono text-right px-2" data-testid={`rev-item-rate-${i}`} />
                        </td>
                        <td className="p-1.5">
                          <Input type="number" value={it.gst_rate} onChange={(e) => updItem(i, "gst_rate", e.target.value)} className="h-8 text-xs mono text-right px-2" data-testid={`rev-item-gst-${i}`} />
                        </td>
                        <td className="p-1.5 mono text-right tabular font-semibold">
                          {amt.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="p-1 text-center">
                          <button
                            className="text-zinc-400 hover:text-red-600"
                            onClick={() => removeItem(i)}
                            data-testid={`rev-item-remove-${i}`}
                            title="Remove this line item"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {bill.line_items.length === 0 && (
                    <tr>
                      <td colSpan="8" className="p-6 text-center text-zinc-400 text-xs">
                        No line items. Click Add Row.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Section>

          {/* Tax type + charges */}
          <Section title="Tax Type & Charges">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Tax Type</Label>
                <select
                  value={bill.tax_type}
                  onChange={(e) => upd("tax_type", e.target.value)}
                  className="h-9 text-sm w-full rounded-md border border-input bg-transparent px-3 mt-1"
                  data-testid="rev-tax-type"
                >
                  <option value="auto">Auto (based on state codes)</option>
                  <option value="igst">IGST (Inter-state)</option>
                  <option value="cgst_sgst">CGST + SGST (Intra-state)</option>
                </select>
              </div>
              {show(bill.freight_charges) && (
                <F label="Freight Charges (₹)" value={bill.freight_charges} onChange={(v) => upd("freight_charges", v)} testid="rev-freight" />
              )}
              {show(bill.packing_charges) && (
                <F label="Packing Charges (₹)" value={bill.packing_charges} onChange={(v) => upd("packing_charges", v)} testid="rev-packing" />
              )}
            </div>
          </Section>

          {/* Totals preview */}
          <Section title="Totals (preview)">
            <div className="rounded-md border border-zinc-200 bg-white divide-y divide-zinc-100 text-xs mono tabular">
              <Row label="Subtotal (Taxable)" value={subtotal} />
              <Row label="GST Total" value={totalTax} />
              <Row label="Freight Charges" value={freight} />
              <Row label="Packing Charges" value={packing} />
              <Row label="Grand Total (INR)" value={grandTotal} bold testid="rev-grand-total" />
            </div>
          </Section>

          {/* Notes */}
          {show(bill.notes) && (
            <Section title="Notes (optional)">
              <Textarea
                value={bill.notes}
                onChange={(e) => upd("notes", e.target.value)}
                className="text-xs min-h-[60px]"
                placeholder="Terms, remarks, bank details, etc."
                data-testid="rev-notes"
              />
            </Section>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-zinc-200 px-6 py-3 flex items-center justify-between">
          <div className="text-xs text-zinc-500 flex items-center gap-2">
            <Receipt size={14} />
            Grand Total: <span className="mono font-semibold">
              ₹ {grandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          <Button
            className="bg-violet-600 hover:bg-violet-700"
            onClick={confirmGenerate}
            disabled={confirming}
            data-testid="confirm-generate-btn-bottom"
          >
            <Save size={14} className="mr-1" />
            {confirming ? "Generating…" : "Confirm & Generate Invoice"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, right, children }) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-wider text-zinc-500">{title}</div>
        {right}
      </div>
      {children}
    </Card>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-2 gap-3">{children}</div>;
}

function F({ label, value, onChange, testid, wide }) {
  return (
    <div className={`space-y-1 ${wide ? "col-span-2" : ""}`}>
      <Label className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</Label>
      <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-8 text-xs" data-testid={testid} />
    </div>
  );
}

function Row({ label, value, bold, testid }) {
  return (
    <div className={`flex justify-between px-3 py-1.5 ${bold ? "bg-zinc-50 font-bold" : ""}`}>
      <span className="font-sans text-zinc-600">{label}</span>
      <span data-testid={testid}>₹ {Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
    </div>
  );
}
